/**
 * chain-eos/session.ts
 *
 * WharfKit SessionKit setup — реплицирует архитектуру webdex (paycashwebswap),
 * дополненную паттернами Greymass Unicove.
 *
 * transactPlugins идут во ВТОРОЙ аргумент (options), а не в первый (args).
 * Порядок плагинов важен:
 *   1. TransactPluginResourceProvider — Greymass Fuel
 *   2. TransactPluginAutoCorrect     — автоматический PowerUp как fallback
 */

import * as pako from 'pako'
import {
  SessionKit,
  type SerializedSession,
  type Session,
  type TransactArgs,
  type TransactResult,
} from '@wharfkit/session'
import { WebRenderer } from '@wharfkit/web-renderer'
import { TransactPluginResourceProvider } from '@wharfkit/transact-plugin-resource-provider'
import { TransactPluginAutoCorrect } from '@wharfkit/transact-plugin-autocorrect'
import { secureGet, secureRemove, secureSet } from '@properwallet/wallet-core'
import { EOS_CHAIN_ID, EOS_RPC_ENDPOINTS, FUEL_URL } from './config.js'

// ─── pako/zlib bootstrap ──────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).zlib = pako
}

// ─── Re-export Session type for use in other packages ────────────────────────
export type { Session }

// ─── Transact plugins (Greymass middleware) ────────────────────────────────────

// Fuel (`res_quest_transaction` and friends) is a Greymass-specific API — it
// does not exist on other RPC nodes. Pointing this at EOS_RPC_ENDPOINTS[0]
// (currently eosusa.io, chosen for plain chain reads/failover) made every
// PowerUp/transfer that needed Fuel hit a 404 on eosusa and hang inside
// session.transact, since the resource-provider plugin never got a response
// to resolve or reject on. Fuel must always go to FUEL_URL, independent of
// which node EOS_RPC_ENDPOINTS[0] happens to be this week.
export const resourceProviderPlugin = new TransactPluginResourceProvider({
  endpoints: {
    [EOS_CHAIN_ID]: FUEL_URL,
  },
  allowFees: true,
})

export const autocorrectPlugin = new TransactPluginAutoCorrect()

// ─── SessionKit singleton ─────────────────────────────────────────────────────

export interface WharfState {
  sessionKit: SessionKit | null
}

export const wharf: WharfState = {
  sessionKit: null,
}

/**
 * Инициализировать SessionKit — вызывается один раз при старте приложения.
 * transactPlugins передаются в options (второй аргумент) — это требование WharfKit 1.6.x
 */
export function initSessionKit(
  walletPlugins: ConstructorParameters<typeof SessionKit>[0]['walletPlugins'],
  rpcUrl?: string,
): SessionKit {
  if (wharf.sessionKit) return wharf.sessionKit

  const url = rpcUrl ?? EOS_RPC_ENDPOINTS[0]

  const kit = new SessionKit(
    {
      appName: 'ProperWallet',
      chains: [{ id: EOS_CHAIN_ID, url }],
      ui: new WebRenderer(),
      walletPlugins,
    },
    {
      // transactPlugins go in OPTIONS (2nd arg), not in args
      transactPlugins: [resourceProviderPlugin, autocorrectPlugin],
    },
  )

  wharf.sessionKit = kit
  return kit
}

export function resetSessionKit(): void {
  wharf.sessionKit = null
}

// ─── Session restore ──────────────────────────────────────────────────────────

const WHARF_SESSION_STORAGE_PREFIX = 'wharf_session_'

export function wharfSessionStorageKey(accountName: string): string {
  return `${WHARF_SESSION_STORAGE_PREFIX}${accountName.trim()}`
}

export async function persistWharfSession(session: Session): Promise<void> {
  const serialized = session.serialize()
  await secureSet(wharfSessionStorageKey(String(serialized.actor)), JSON.stringify(serialized))

  const kit = wharf.sessionKit
  if (kit) {
    try {
      await kit.persistSession(session, true)
    } catch (e) {
      console.warn('[session] SessionKit persist failed:', e)
    }
  }
}

export async function clearPersistedWharfSession(accountName: string): Promise<void> {
  await secureRemove(wharfSessionStorageKey(accountName))
}

export async function restoreSession(accountName?: string): Promise<Session | null> {
  const kit = await waitForSessionKit()
  if (!kit) return null
  try {
    if (accountName) {
      const raw = await secureGet(wharfSessionStorageKey(accountName))
      if (raw) {
        const serialized = JSON.parse(raw) as SerializedSession
        return (await kit.restore(serialized)) ?? null
      }
    }
    return (await kit.restore()) ?? null
  } catch (e) {
    console.warn('[session] restore failed:', e)
    return null
  }
}

export async function waitForSessionKit(maxMs = 8000): Promise<SessionKit | null> {
  const start = Date.now()
  while (Date.now() - start < maxMs) {
    if (wharf.sessionKit) return wharf.sessionKit
    await new Promise(r => setTimeout(r, 50))
  }
  return wharf.sessionKit
}

export async function transact(
  session: Session,
  args: TransactArgs,
): Promise<TransactResult> {
  return session.transact(args)
}

export async function getAccountInfo(accountName: string) {
  const { APIClient } = await import('@wharfkit/antelope')
  const client = new APIClient({ url: EOS_RPC_ENDPOINTS[0] })
  return client.v1.chain.get_account(accountName)
}
